import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Calendar, Building2, Mail, Check, MoreVertical, Star } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { AdvertisementCategory, Advertisement } from '../types';
import { pbClickTracker } from '../services/pocketbaseClickTracking';
// Removed insecure client-side generation - now using secure server endpoint
import { getTranslations, getCategoryTranslation, getLocaleFromRegion } from '../utils/translations';
import { PocketBaseDataService as DataService, PromoRequestStatsResponse } from '../services/pocketbaseDataService';
import { getPartnerLogoUrl } from '../utils/pocketbaseHelpers';
import { formatDescriptionHtml } from '../utils/formatDescription';
import { NewsletterSubscription } from './NewsletterSubscription';
import { preloadImage } from '../utils/imageCache';
import { useRecaptcha } from '../hooks/useRecaptcha';

const parseCampaignAdIds = (adIds: any): string[] => {
  if (Array.isArray(adIds)) {
    return adIds;
  }
  if (typeof adIds === 'string') {
    try {
      const parsed = JSON.parse(adIds);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

export const PublicCampaignView: React.FC = () => {
  useEffect(() => {
    const previousFavicons = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]'));
    const customFavicon = document.createElement('link');
    customFavicon.rel = 'icon';
    customFavicon.type = 'image/svg+xml';
    customFavicon.href = '/favicon-public.svg';

    document.head.appendChild(customFavicon);

    return () => {
      document.head.removeChild(customFavicon);
      previousFavicons.forEach(favicon => document.head.appendChild(favicon));
    };
  }, []);
  const { campaignId } = useParams<{ campaignId: string }>();
  const [searchParams] = useSearchParams();
  const excludePartnerId = searchParams.get('domainuuid');
  
  const { 
    marketingCampaigns, 
    partnerCampaigns, 
    advertisements, 
    partners,
    filters,
    loading 
  } = useAuth();
  
  const [campaign, setCampaign] = useState<any>(null);
  const [displayCampaigns, setDisplayCampaigns] = useState<any[]>([]);
  const [showPromoForm, setShowPromoForm] = useState<string | null>(null);
  const [selectedAd, setSelectedAd] = useState<any>(null);
  const [email, setEmail] = useState('');
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [submittedRequests, setSubmittedRequests] = useState<Set<string>>(new Set());
  const [selectedAdForSession, setSelectedAdForSession] = useState<string | null>(() => {
    return sessionStorage.getItem('selectedAdvertisementId');
  });
  const [selectedCategory, setSelectedCategory] = useState<AdvertisementCategory | 'top_10' | null>(null);
  const [adSortOrder, setAdSortOrder] = useState<string[]>(() => {
    const stored = sessionStorage.getItem('adSortOrder');
    return stored ? JSON.parse(stored) : [];
  });
  const [showPrefetchOverlay, setShowPrefetchOverlay] = useState(true);
  const [progress, setProgress] = useState(0);
  const [fakeOfferCount, setFakeOfferCount] = useState(() => Math.floor(Math.random() * 70) + 1);
  const [finalOfferCount, setFinalOfferCount] = useState<number | null>(null);
  const [generatedPromocode, setGeneratedPromocode] = useState<string | null>(null);
  const [isDirectContactMessage, setIsDirectContactMessage] = useState(false);
  const [promoDeliveryNotice, setPromoDeliveryNotice] = useState<{
    title?: string;
    message: string;
    type: 'success' | 'info' | 'error';
    placement?: 'global' | 'modal';
  } | null>(null);
  const [promoEmailReminder, setPromoEmailReminder] = useState<string | null>(null);
  const [promoTargetUrl, setPromoTargetUrl] = useState<string | null>(null);
  const [showPromocodeModal, setShowPromocodeModal] = useState(false);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [showMoreCategories, setShowMoreCategories] = useState(false);
  const [showNoPromoConfirm, setShowNoPromoConfirm] = useState(false);
  const [pendingNoPromoRequest, setPendingNoPromoRequest] = useState<{ partnerCampaignId: string; partnerId: string } | null>(null);
  const [showRedirectConfirm, setShowRedirectConfirm] = useState(false);
  const [pendingRedirectRequest, setPendingRedirectRequest] = useState<{ partnerCampaignId: string; partnerId: string; redirectUrl: string } | null>(null);
  const [imagesReady, setImagesReady] = useState(false);
  const [forceContinue, setForceContinue] = useState(false);
  const [promoRequestStats, setPromoRequestStats] = useState<PromoRequestStatsResponse | null>(null);
  const hasTrackedPublicLink = useRef(false); // Prevent duplicate tracking
  const lastRandomBucketRef = useRef(-1);
  const { executeRecaptcha, isConfigured: recaptchaConfigured } = useRecaptcha();

  useEffect(() => {
    let cancelled = false;

    const fetchPromoStats = async () => {
      const { data, error } = await DataService.getPromoRequestStats(7);
      if (cancelled) return;
      if (error) {
        console.error('Failed to fetch promo request stats:', error);
        setPromoRequestStats(null);
      } else {
        setPromoRequestStats(data);
      }
    };

    fetchPromoStats();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!showPrefetchOverlay) {
      return;
    }
    const duration = 3000;
    const start = performance.now();
    let animationFrame: number;

    const animate = (now: number) => {
      const elapsed = now - start;
      const nextProgress = Math.min(100, (elapsed / duration) * 100);
      setProgress(nextProgress);
      if (nextProgress < 100) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [showPrefetchOverlay]);

  // Safety timeout: allow progressing after 6s even if images still loading
  useEffect(() => {
    if (!showPrefetchOverlay) {
      setForceContinue(false);
      return;
    }
    setForceContinue(false);
    const timeoutId = setTimeout(() => {
      setForceContinue(true);
    }, 6000);
    return () => clearTimeout(timeoutId);
  }, [showPrefetchOverlay]);

  useEffect(() => {
    if (!showPrefetchOverlay || progress >= 100) {
      return;
    }
    const bucket = Math.floor(progress / 5);
    if (bucket !== lastRandomBucketRef.current) {
      lastRandomBucketRef.current = bucket;
      setFakeOfferCount(Math.floor(Math.random() * 70) + 1);
    }
  }, [showPrefetchOverlay, progress]);

  useEffect(() => {
    if (loading) {
      setImagesReady(false);
      return;
    }

    const now = new Date();
    const adMap = new Map(advertisements.map(ad => [ad.id, ad]));
    const uniqueAds = new Map<string, typeof advertisements[0]>();

    displayCampaigns.forEach(pc => {
      const campaignAdIds = parseCampaignAdIds(pc.advertisement_ids);
      if (campaignAdIds.length === 0) {
        return;
      }

      campaignAdIds.forEach(adId => {
        const ad = adMap.get(adId);
        if (!ad) {
          return;
        }
        if (ad.status !== 'active') {
          return;
        }
        if (ad.approval_status !== 'approved') {
          return;
        }
        if (ad.region !== pc.region) {
          return;
        }
        if (ad.end_date && new Date(ad.end_date) < now) {
          return;
        }
        if (!uniqueAds.has(ad.id)) {
          uniqueAds.set(ad.id, ad);
        }
      });
    });

    setFinalOfferCount(uniqueAds.size);

    const bannerUrls = Array.from(uniqueAds.values())
      .map(ad => ad.banner_url)
      .filter((url): url is string => Boolean(url));

    if (bannerUrls.length === 0) {
      setImagesReady(true);
      return;
    }

    setImagesReady(false);
    let cancelled = false;
    Promise.all(bannerUrls.map(url => preloadImage(url))).then(() => {
      if (!cancelled) {
        setImagesReady(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [loading, displayCampaigns, advertisements]);

  // Get locale from campaign or region - determine early to prevent language flash
  const locale = useMemo(() => {
    // 1. If campaign is already loaded, use its locale (highest priority)
    if (campaign?.locale) {
      // Store for future page loads
      sessionStorage.setItem(`campaign_locale_${campaignId}`, campaign.locale);
      return campaign.locale;
    }
    if (campaign?.region) {
      const regionLocale = getLocaleFromRegion(campaign.region);
      sessionStorage.setItem(`campaign_locale_${campaignId}`, regionLocale);
      return regionLocale;
    }
    
    // 2. Try to get from marketing campaigns
    if (campaignId && marketingCampaigns.length > 0) {
      const foundCampaign = marketingCampaigns.find(c => c.id === campaignId);
      if (foundCampaign) {
        const mcLocale = foundCampaign.locale || (foundCampaign.region ? getLocaleFromRegion(foundCampaign.region) : null);
        if (mcLocale) {
          sessionStorage.setItem(`campaign_locale_${campaignId}`, mcLocale);
          return mcLocale;
        }
      }
    }
    
    // 3. Check URL params for locale hint
    const urlLocale = searchParams.get('locale');
    if (urlLocale && (urlLocale === 'bg' || urlLocale === 'en')) {
      return urlLocale;
    }
    
    // 4. Check sessionStorage for previously determined locale for this campaign
    if (campaignId) {
      const storedLocale = sessionStorage.getItem(`campaign_locale_${campaignId}`);
      if (storedLocale) {
        return storedLocale;
      }
    }
    
    // 5. Detect from browser language (for Bulgarian users)
    const browserLang = navigator.language || (navigator as any).userLanguage || '';
    if (browserLang.toLowerCase().startsWith('bg')) {
      return 'bg';
    }
    
    // 6. Default to English
    return 'en';
  }, [campaign, campaignId, marketingCampaigns, searchParams]);
  
  const t = getTranslations(locale);
  const mostPopularTooltip = locale?.startsWith('bg') ? 'Най-популярен избор' : 'Most popular choice';

  const poweredByContent = useMemo(() => {
    const text = t.poweredBy || '';
    const match = text.match(/Magic Link[s]?/i);

    if (!match) {
      return text;
    }

    const matchedText = match[0];
    const lowerText = text.toLowerCase();
    const startIndex = lowerText.indexOf(matchedText.toLowerCase());

    if (startIndex === -1) {
      return text;
    }

    const before = text.slice(0, startIndex);
    const after = text.slice(startIndex + matchedText.length);

    return (
      <>
        {before}
        <a href={`/contact-us?locale=${locale}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
          {matchedText}
        </a>
        {after}
      </>
    );
  }, [t.poweredBy, locale]);

  // Calculate top 10% ads based on last 7 days promo requests
  const top10PercentAdIds = useMemo(() => {
    if (!promoRequestStats || promoRequestStats.stats.length === 0) {
      return new Set<string>();
    }

    const eligibleAdIds = new Set(
      advertisements
        .filter(ad => ad.status === 'active' && ad.approval_status === 'approved')
        .map(ad => ad.id)
    );

    const eligibleStats = promoRequestStats.stats.filter(stat => eligibleAdIds.has(stat.advertisement_id));
    if (eligibleStats.length === 0) {
      return new Set<string>();
    }

    const top10Count = Math.max(1, Math.ceil(eligibleStats.length * 0.1));
    const topAdIds = new Set(
      [...eligibleStats]
        .sort((a, b) => b.request_count - a.request_count)
        .slice(0, top10Count)
        .map(stat => stat.advertisement_id)
    );

    return topAdIds;
  }, [promoRequestStats, advertisements]);

  // Get available categories from filters collection, only showing those with active ads
  const availableCategories = useMemo(() => {
    // Get all active filter names
    const activeFilterNames = filters
      .filter(f => f.status === 'active')
      .map(f => f.name);
    
    // Get all categories that have at least one active, approved ad in the current campaigns
    const categoriesWithAds = new Set<string>();
    
    displayCampaigns.forEach(pc => {
      const campaignAdIds = parseCampaignAdIds(pc.advertisement_ids);
      campaignAdIds.forEach(adId => {
        const ad = advertisements.find(a => a.id === adId);
        if (ad && 
            ad.status === 'active' && 
            ad.approval_status === 'approved' &&
            ad.region === pc.region &&
            (!ad.end_date || new Date(ad.end_date) >= new Date())) {
          (ad.categories || []).forEach(cat => categoriesWithAds.add(cat));
        }
      });
    });
    
    // Return only filters that exist in the collection AND have ads
    return activeFilterNames.filter(name => categoriesWithAds.has(name));
  }, [filters, displayCampaigns, advertisements]);

  useEffect(() => {
    // Skip if still loading data or if data hasn't been initialized yet
    if (loading || (marketingCampaigns.length === 0 && partners.length === 0)) {
      return;
    }
    
    // Handle marketing campaign route using UUID
    if (campaignId) {
      // Find campaign by UUID (id field)
      const foundCampaign = marketingCampaigns.find(c => c.id === campaignId);
      
      if (!foundCampaign) {
        return;
      }
      
      if (foundCampaign.status !== 'active') {
        return;
      }
      
      setCampaign(foundCampaign);
      
      // Get partner campaigns for this marketing campaign FIRST
      // We need to know which partners' ads will be displayed before tracking
      
      // Only show campaigns from the same region
      let relevantPartnerCampaigns = partnerCampaigns.filter(pc => 
        foundCampaign.partner_campaign_ids.includes(pc.id) && 
        pc.status === 'active' &&
        pc.region === foundCampaign.region // Regional filtering
      );
      
      // If excludePartner parameter is present, filter out that partner's campaigns
      // This is used for partner-specific "magic links"
      if (excludePartnerId) {
        relevantPartnerCampaigns = relevantPartnerCampaigns.filter(pc => 
          pc.partner_id !== excludePartnerId
        );
      }
      
      // Track public link click and record which partners' ads are displayed
      // Only track once per page load to prevent duplicates
      if (excludePartnerId && !hasTrackedPublicLink.current) {
        const linkOwnerExists = partners.some(p => p.id === excludePartnerId);
        if (linkOwnerExists) {
        hasTrackedPublicLink.current = true; // Mark as tracked
        
        // Get list of partners whose ads are being displayed
        const displayedPartnerIds = relevantPartnerCampaigns.map(pc => pc.partner_id);
        
        // Track the public link click with metadata about displayed partners
        pbClickTracker.trackPublicLinkClick(
          excludePartnerId, // Partner who owns the magic link
          foundCampaign.id,
          excludePartnerId, // Partner whose ads are excluded
          { displayed_partner_ids: displayedPartnerIds } // Additional metadata
        ).catch(() => {
          hasTrackedPublicLink.current = false; // Reset on error so it can retry
        });
        }
      }
      
      // Public marketing campaign view: Show partner advertisements (excluding specified partner if any)
      // Sort by priority
      relevantPartnerCampaigns.sort((a, b) => a.priority - b.priority);
      
      setDisplayCampaigns(relevantPartnerCampaigns);
    }
  }, [campaignId, excludePartnerId, marketingCampaigns, partnerCampaigns, advertisements, partners, loading]);

  const getPartner = (partnerId: string) => {
    return partners.find(p => p.id === partnerId);
  };

  // Get provider info for an ad - uses ad's provider info if set, otherwise falls back to partner
  const getProviderInfo = (ad: Advertisement) => {
    const partner = getPartner(ad.partner_id);
    const hasCustomProvider = ad.provider_entity_id || ad.provider_name;
    
    return {
      name: hasCustomProvider && ad.provider_name ? ad.provider_name : partner?.name || '',
      logoUrl: hasCustomProvider && ad.provider_logo_url ? ad.provider_logo_url : (partner ? getPartnerLogoUrl(partner) : ''),
      privacyPolicyUrl: hasCustomProvider && ad.provider_privacy_policy_url ? ad.provider_privacy_policy_url : partner?.privacy_policy_url
    };
  };

  const handleConfirmNoPromo = () => {
    if (!pendingNoPromoRequest) return;
    const { partnerCampaignId, partnerId } = pendingNoPromoRequest;
    setShowNoPromoConfirm(false);
    setPendingNoPromoRequest(null);
    handlePromoRequest(partnerCampaignId, partnerId, { skipNoPromoConfirmation: true });
  };

  const handleCancelNoPromo = () => {
    setShowNoPromoConfirm(false);
    setPendingNoPromoRequest(null);
    setShowPromoForm(null);
    setSelectedAd(null);
  };

  const handleConfirmRedirect = () => {
    if (!pendingRedirectRequest) return;
    const { partnerCampaignId, partnerId } = pendingRedirectRequest;
    setShowRedirectConfirm(false);
    setPendingRedirectRequest(null);
    handlePromoRequest(partnerCampaignId, partnerId, { skipRedirectConfirmation: true });
  };

  const handleCancelRedirect = () => {
    setShowRedirectConfirm(false);
    setPendingRedirectRequest(null);
    setShowPromoForm(null);
    setSelectedAd(null);
  };

  const toggleCategory = (category: AdvertisementCategory | 'top_10') => {
    setSelectedCategory(prev => 
      prev === category ? null : category
    );
  };

  // Remove the old filteredAds logic since we now filter at advertisement level
  // const filteredAds = displayCampaigns;



  const handlePromoRequest = async (
    partnerCampaignId: string,
    partnerId: string,
    options?: { skipNoPromoConfirmation?: boolean; skipRedirectConfirmation?: boolean }
  ) => {
    if (!email.trim()) {
      setPromoDeliveryNotice({
        title: t.missingEmailTitle,
        message: t.missingEmailMessage,
        type: 'error',
        placement: 'modal'
      });
      return;
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email.trim())) {
      setPromoDeliveryNotice({
        title: t.invalidEmailTitle,
        message: t.invalidEmailMessage,
        type: 'error',
        placement: 'modal'
      });
      setEmail('');
      return;
    }
    if (!agreedToPrivacy) {
      setPromoDeliveryNotice({
        title: t.privacyConsentReminderTitle,
        message: t.privacyConsentReminderMessage,
        type: 'error',
        placement: 'modal'
      });
      return;
    }
    
    if (email && agreedToPrivacy && selectedAd) {
      // Check if ad has promocode using public flags
      const hasPromocode = selectedAd.has_promocode;

      // If no promocode, show confirmation modal
      if (!hasPromocode && !options?.skipNoPromoConfirmation) {
        setPendingNoPromoRequest({ partnerCampaignId, partnerId });
        setShowNoPromoConfirm(true);
        return;
      }
      
      // Request promocode from secure server endpoint
      try {
        setPromoDeliveryNotice(null);
        
        // Get reCAPTCHA token if configured
        let recaptchaToken: string | null = null;
        if (recaptchaConfigured) {
          recaptchaToken = await executeRecaptcha('request_promocode');
          if (!recaptchaToken) {
            console.warn('[reCAPTCHA] Failed to get token, proceeding without it');
          }
        }
        
        const { data: promoResponse, error: promoError } = await DataService.requestPromocode(
          selectedAd.id,
          email,
          agreedToPrivacy,
          recaptchaToken
        );
        
        if (promoError) {
          alert(promoError);
          setShowPromoForm(null);
          return;
        }
        
        // Handle redirect response
        if (promoResponse?.redirect_url) {
          // Show confirmation modal for redirect if not already confirmed
          if (!options?.skipRedirectConfirmation) {
            setPendingRedirectRequest({ 
              partnerCampaignId, 
              partnerId, 
              redirectUrl: promoResponse.redirect_url 
            });
            setShowRedirectConfirm(true);
            return;
          }
          
          // Store selected advertisement in session
          setSelectedAdForSession(selectedAd.id);
          sessionStorage.setItem('selectedAdvertisementId', selectedAd.id);
          sessionStorage.setItem('selectedAdvertisementData', JSON.stringify({
            advertisementId: selectedAd.id,
            advertisementName: selectedAd.name,
            partnerCampaignId: partnerCampaignId,
            partnerId: partnerId,
            email: email,
            promocode: 'REDIRECT',
            submittedAt: new Date().toISOString()
          }));

          // Track promo request
          await pbClickTracker.trackPromoRequest(
            partnerId,
            partnerCampaignId,
            selectedAd.id,
            email,
            campaign?.id
          );

          setSubmittedRequests(prev => new Set([...prev, partnerCampaignId]));
          setShowPromoForm(null);

          // Redirect
          window.location.href = promoResponse.redirect_url;
          return;
        }
        
        // Handle promocode response
        const deliveryMethods = promoResponse?.delivery_methods;
        const shouldDisplayOnWeb = deliveryMethods?.web_display !== false;
        const promocode = promoResponse?.promo_code ?? null;
        const messageText = promoResponse?.message || '';
        const emailDelivery = promoResponse?.email_delivery;
        const targetUrl = promoResponse?.target_url ?? null; // Partner's page URL
      
        // Store selected advertisement in session
        setSelectedAdForSession(selectedAd.id);
        sessionStorage.setItem('selectedAdvertisementId', selectedAd.id);
        sessionStorage.setItem('selectedAdvertisementData', JSON.stringify({
          advertisementId: selectedAd.id,
          advertisementName: selectedAd.name,
          partnerCampaignId: partnerCampaignId,
          partnerId: partnerId,
          email: email,
          promocode: promocode,
          submittedAt: new Date().toISOString()
        }));
      
        // Track promo request
        await pbClickTracker.trackPromoRequest(
          partnerId,
          partnerCampaignId,
          selectedAd.id,
          email,
          campaign?.id
        );
        
        // Note: The server endpoint already created the promo_request record
        // We just need to update UI state
        setSubmittedRequests(prev => new Set([...prev, partnerCampaignId]));
        setShowPromoForm(null);
        
        const emailAndWeb = shouldDisplayOnWeb && deliveryMethods?.email;

        if (!shouldDisplayOnWeb && deliveryMethods?.email) {
          setGeneratedPromocode(null);
          setIsDirectContactMessage(false);
          setShowPromocodeModal(false);
          setPromoDeliveryNotice({
            title: emailDelivery?.sent ? t.promoSentEmailTitle : undefined,
            message: messageText || (emailDelivery?.sent
              ? t.promoSentEmailDescription.replace('{email}', email)
              : t.apiRequestSentDescription),
            type: emailDelivery?.sent ? 'success' : 'info',
            placement: 'modal'
          });
          setPromoEmailReminder(null);
        } else if (promocode) {
          setPromoDeliveryNotice(null);
          setGeneratedPromocode(promocode);
          setIsDirectContactMessage(messageText.toLowerCase().includes('partner will contact you'));
          setShowPromocodeModal(true);
          setPromoEmailReminder(emailAndWeb ? t.promoEmailReminderMessage : null);
          setPromoTargetUrl(targetUrl);
        } else {
          setPromoDeliveryNotice(messageText ? { message: messageText, type: 'info', placement: 'modal' } : null);
          setGeneratedPromocode(null);
          setShowPromocodeModal(false);
          setPromoEmailReminder(null);
        }
      } catch (error: any) {
        alert(error.message || 'Failed to request promocode. Please try again.');
        setShowPromoForm(null);
      }
    }
  };
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    });
  };

  const closePromocodeModal = () => {
    setShowPromocodeModal(false);
    setGeneratedPromocode(null);
    setCopiedToClipboard(false);
    setIsDirectContactMessage(false);
    setSelectedAd(null);
    setShowPromoForm(null);
    setPromoEmailReminder(null);
    setPromoTargetUrl(null);
  };

  const getCampaignAds = (partnerCampaign: any) => {
    const ads = advertisements.filter(ad => 
      partnerCampaign.advertisement_ids.includes(ad.id) && 
      ad.status === 'active' &&
      ad.approval_status === 'approved' && // Only show explicitly approved advertisements (not pending/empty)
      // Only check end_date if it's set and valid
      (!ad.end_date || ad.end_date === '' || new Date(ad.end_date) >= new Date()) &&
      // REGION FILTER: Only show ads from the same region as the partner campaign
      ad.region === partnerCampaign.region &&
      // ADVERTISEMENT MODE FILTER: Exclude independent ads (they're only accessible via direct link)
      // Allow default and universal modes in campaigns
      (ad.advertisement_mode === 'default' || ad.advertisement_mode === 'universal' || !ad.advertisement_mode)
    );
    
    // Sort by priority first, then by stored order or random for new sessions
    const sortedAds = ads.sort((a, b) => {
      if (a.priority !== b.priority) {
        // Sort by priority: platinum > gold > silver > bronze
        const priorityOrder = { platinum: 1, gold: 2, silver: 3, bronze: 4 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      }
      
      // Use stored order if available, otherwise random
      const aIndex = adSortOrder.indexOf(a.id);
      const bIndex = adSortOrder.indexOf(b.id);
      
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex; // Use stored order
      } else if (aIndex !== -1) {
        return -1; // a is in stored order, b is not
      } else if (bIndex !== -1) {
        return 1; // b is in stored order, a is not
      } else {
        // Both are new, use consistent order based on ID
        return a.id.localeCompare(b.id);
      }
    });
    
    return sortedAds;
  };
  const overlayReady = progress >= 100 && finalOfferCount !== null && !loading && (imagesReady || forceContinue);
  const roundedProgress = Math.round(progress);
  const progressLabel = (t.prefetchProgressLabel || '{percent}% complete').replace('{percent}', `${roundedProgress}`);

  if (showPrefetchOverlay) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center px-4">
        <div className="max-w-lg w-full bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-3xl p-8 shadow-2xl text-white text-center border border-white/20">
          <p className="uppercase tracking-[0.3em] text-white/70 text-xs mb-4">{t.prefetchBadge}</p>
          <h1 className="text-2xl font-semibold mb-6">{t.prefetchTitle}</h1>
          <div className="text-7xl font-bold mb-2">
            {overlayReady && finalOfferCount !== null ? finalOfferCount : fakeOfferCount}
          </div>
          <p className="text-white/80 mb-8">
            {overlayReady && finalOfferCount !== null ? (t.prefetchOffersReady || 'Offers unlocked for you') : (t.prefetchOffersPending || 'Potential offers just for you')}
          </p>

          <div className="w-full bg-white/20 rounded-full h-3 mb-3">
            <div
              className="h-3 rounded-full bg-gradient-to-r from-yellow-300 via-orange-400 to-pink-500 transition-all duration-100 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-sm text-white/70 mb-8">{progressLabel}</p>

          <button
            disabled={!overlayReady}
            onClick={() => setShowPrefetchOverlay(false)}
            className={`w-full py-3 rounded-2xl font-semibold transition ${
              overlayReady
                ? 'bg-white text-purple-700 hover:bg-purple-50'
                : 'bg-white/30 text-white cursor-wait'
            }`}
          >
            {overlayReady ? (t.prefetchButtonReady || 'Go to offers') : (t.prefetchButtonPending || 'Preparing...')}
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t.loadingCampaign}</p>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl text-gray-300 mb-4">🔍</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t.campaignNotFound}</h1>
          <p className="text-gray-600">
            {t.campaignNotFoundDescription}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img
                src="/favicon-public.svg"
                alt="Click & Claim logo"
                className="w-10 h-10"
              />
              <div className="ml-3">
                <h1 className="text-lg md:text-xl font-semibold text-gray-800">Click and Claim ✨</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Partner Campaigns Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-12 min-h-screen">
        {displayCampaigns.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl text-gray-300 mb-4">📢</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t.noActiveOffers}</h2>
            <p className="text-gray-600">
              {t.noActiveOffersDescription}
            </p>
          </div>
        ) : (
          <>
            <div className="text-center mb-0 bg-gradient-to-r from-blue-50 to-purple-50 pb-4 rounded-2xl">
              <h2 className="text-2xl md:text-3xl font-bold text-purple-600 mb-1.5 px-4">{t.exclusiveOffersTitle || 'Your Exclusive Offers Await'}</h2>
              <p className="text-gray-600 mx-auto px-4 text-sm md:text-base leading-relaxed hidden md:block">
                {t.exclusiveOffersDescription || 'Click on any offer below to claim your special gift from our trusted partners'}
              </p>
            </div>

            {/* Customer Step-by-Step Guide */}
            <div className="mb-8 hidden md:block">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                
                {/* Circles and Lines Row */}
                <div className="flex items-center justify-center mb-4">
                  <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                    1
                  </div>
                  <div className="h-1 w-16 sm:w-24 md:w-32 lg:w-40 xl:w-[340px] bg-gradient-to-r from-blue-500 to-green-500 flex-shrink-0"></div>
                  <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                    2
                  </div>
                  <div className="h-1 w-16 sm:w-24 md:w-32 lg:w-40 xl:w-[340px] bg-gradient-to-r from-green-500 to-purple-500 flex-shrink-0"></div>
                  <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                    3
                  </div>
                </div>
                
                {/* Text Row */}
                <div className="flex items-start justify-center">
                  <div className="flex-1 text-center px-2">
                    <div className="text-sm font-semibold text-gray-900 mb-1">{t.step1Title || 'Choose Offer'}</div>
                  </div>
                  <div className="flex-1 text-center px-2">
                    <div className="text-sm font-semibold text-gray-900 mb-1">{t.step2Title || 'Enter Email'}</div>
                  </div>
                  <div className="flex-1 text-center px-2">
                    <div className="text-sm font-semibold text-gray-900 mb-1">{t.step3Title || 'Get Code'}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Category Filters - First line shows visible categories, 3-dot expands to show all */}
            {availableCategories.length > 0 && (
              <div className="mb-8">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
                  {/* Mobile: Horizontal scrollable */}
                  <div className="md:hidden">
                    <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                      {/* Top 10% Filter */}
                      {top10PercentAdIds.size > 0 && (
                        <button
                          onClick={() => toggleCategory('top_10')}
                          title={mostPopularTooltip}
                          aria-label={mostPopularTooltip}
                          className={`px-3 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 flex items-center snap-start ${
                            selectedCategory === 'top_10'
                              ? 'bg-yellow-500 text-white shadow-lg'
                              : 'bg-yellow-100 text-yellow-800 active:bg-yellow-200 border border-yellow-300'
                          }`}
                        >
                          <Star size={16} fill={selectedCategory === 'top_10' ? 'white' : '#eab308'} />
                        </button>
                      )}
                      {/* All Categories */}
                      {availableCategories.map((category) => (
                        <button
                          key={category}
                          onClick={() => toggleCategory(category)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 snap-start ${
                            selectedCategory === category
                              ? 'bg-blue-600 text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 active:bg-gray-200'
                          }`}
                        >
                          {getCategoryTranslation(category, locale)}
                        </button>
                      ))}
                      {/* Clear Filter */}
                      {selectedCategory && (
                        <button
                          onClick={() => setSelectedCategory(null)}
                          className="px-4 py-2 rounded-full text-sm font-medium bg-red-100 text-red-700 active:bg-red-200 whitespace-nowrap flex-shrink-0 snap-start"
                        >
                          {t.clearFilter}
                        </button>
                      )}
                    </div>
                    {/* Scroll hint for mobile */}
                    <div className="text-xs text-gray-400 text-center mt-2">{t.swipeHint}</div>
                  </div>

                  {/* Desktop: Three-part layout - Star (left), Categories (center), Dots (right) */}
                  <div className="hidden md:block">
                    <div className="flex items-center justify-between">
                      {/* Left section: Star category */}
                      <div className="flex-shrink-0">
                        {top10PercentAdIds.size > 0 && (
                          <button
                            onClick={() => toggleCategory('top_10')}
                            title={mostPopularTooltip}
                            aria-label={mostPopularTooltip}
                            className={`px-3 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap flex items-center ${
                              selectedCategory === 'top_10'
                                ? 'bg-yellow-500 text-white shadow-lg'
                                : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 border border-yellow-300'
                            }`}
                          >
                            <Star size={16} fill={selectedCategory === 'top_10' ? 'white' : '#eab308'} />
                          </button>
                        )}
                      </div>

                      {/* Center section: Main categories distributed by width */}
                      <div className="flex-1 flex justify-center">
                        <div className="flex gap-2 max-w-full overflow-hidden">
                          {availableCategories.slice(0, 8).map((category) => (
                            <button
                              key={category}
                              onClick={() => toggleCategory(category)}
                              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                                selectedCategory === category
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {getCategoryTranslation(category, locale)}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Right section: 3-dot button */}
                      <div className="flex-shrink-0">
                        {availableCategories.length > 8 && (
                          <button
                            onClick={() => setShowMoreCategories(!showMoreCategories)}
                            className={`p-2 rounded-full transition-colors ${
                              showMoreCategories
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                            title={t.filterByCategories || 'More categories'}
                          >
                            <MoreVertical size={20} />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Expanded section showing REMAINING categories (after first 8) */}
                    {showMoreCategories && availableCategories.length > 8 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex flex-wrap justify-center gap-2">
                          {availableCategories.slice(8).map((category) => (
                            <button
                              key={category}
                              onClick={() => toggleCategory(category)}
                              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap flex-1 min-w-[120px] max-w-[200px] ${
                                selectedCategory === category
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {getCategoryTranslation(category, locale)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Get all advertisements from all campaigns and sort by priority globally */}
            {(() => {
              const allAdsWithDuplicates = displayCampaigns.flatMap(partnerCampaign => 
                getCampaignAds(partnerCampaign).map(ad => ({
                  ...ad,
                  partnerCampaign,
                  isSubmitted: submittedRequests.has(partnerCampaign.id)
                }))
              );
              
              // Deduplicate ads by ID (same ad might be in multiple partner campaigns)
              const seenAdIds = new Set<string>();
              const allAds = allAdsWithDuplicates.filter(adWithCampaign => {
                if (seenAdIds.has(adWithCampaign.id)) {
                  return false; // Skip duplicate
                }
                seenAdIds.add(adWithCampaign.id);
                return true;
              });

              // Filter advertisements by selected category/tags or top 10%
              const filteredAdsByCategory = selectedCategory === 'top_10'
                ? allAds.filter((adWithCampaign: any) => top10PercentAdIds.has(adWithCampaign.id))
                : selectedCategory 
                  ? allAds.filter((adWithCampaign: any) => 
                      adWithCampaign.categories && adWithCampaign.categories.includes(selectedCategory)
                    )
                  : allAds;
              
              // Sort filtered ads globally by priority (platinum, gold, silver, bronze) with session-persistent random order
              const sortedAds = filteredAdsByCategory.sort((a, b) => {
                if (a.priority !== b.priority) {
                  // Sort by priority: platinum > gold > silver > bronze
                  const priorityOrder = { platinum: 1, gold: 2, silver: 3, bronze: 4 };
                  return priorityOrder[a.priority] - priorityOrder[b.priority];
                }
                
                // Use stored order if available, otherwise create random order
                const aIndex = adSortOrder.indexOf(a.id);
                const bIndex = adSortOrder.indexOf(b.id);
                
                if (aIndex !== -1 && bIndex !== -1) {
                  return aIndex - bIndex; // Use stored order
                } else if (aIndex !== -1) {
                  return -1; // a is in stored order, b is not
                } else if (bIndex !== -1) {
                  return 1; // b is in stored order, a is not
                } else {
                  // Both are new, use consistent order based on ID
                  return a.id.localeCompare(b.id);
                }
              });

              // Store the order if not already stored
              if (adSortOrder.length === 0 && filteredAdsByCategory.length > 0) {
                // Create random order for first visit
                const randomOrder = [...filteredAdsByCategory]
                  .sort(() => Math.random() - 0.5)
                  .map(ad => ad.id);
                setAdSortOrder(randomOrder);
                sessionStorage.setItem('adSortOrder', JSON.stringify(randomOrder));
              }
              
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 min-h-[200px]">
                  {sortedAds.map((adWithCampaign) => {
                    const { partnerCampaign, isSubmitted, ...ad } = adWithCampaign;
                    const partner = getPartner(partnerCampaign.partner_id);
                    
                    return (
                      <div 
                        key={ad.id} 
                        className={`bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition duration-300 transform hover:-translate-y-1 cursor-pointer ${
                          selectedAdForSession && selectedAdForSession !== ad.id ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        onClick={async () => {
                          if (!selectedAdForSession || selectedAdForSession === ad.id) {
                            setSelectedAd(adWithCampaign);
                            setShowPromoForm(adWithCampaign.partnerCampaign.id);
                            
                            // Track advertisement view
                            await pbClickTracker.trackAdvertisementView(
                              partnerCampaign.partner_id,
                              partnerCampaign.id,
                              ad.id,
                              campaign?.id,
                              {
                                campaign_name: campaign?.name,
                                advertisement_name: ad.name,
                                partner_name: partner?.name || 'Unknown'
                              }
                            );
                          }
                        }}
                      >
                        {/* Desktop Layout */}
                        <div className="hidden md:flex h-full flex-col">
                          {/* Banner with 5x indent */}
                          <div className="p-1.5 flex-1 flex flex-col">
                            <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden mb-3 relative">
                              {ad.banner_url ? (
                                <img 
                                  src={ad.banner_url} 
                                  alt={ad.name}
                                  loading="lazy"
                                  decoding="async"
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    target.nextElementSibling?.classList.remove('hidden');
                                  }}
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
                                  <Building2 className="text-purple-600" size={48} />
                                </div>
                              )}
                              
                              {/* Top 10% Star Badge in top-left corner */}
                              {top10PercentAdIds.has(ad.id) && (
                                <Star
                                  size={22}
                                  fill="#facc15"
                                  stroke="#ca8a04"
                                  className="absolute top-2 left-2 drop-shadow-lg"
                                />
                              )}
                              
                              {/* Badge in top-right corner */}
                              {ad.badge_text && (
                                <div className={`absolute top-2 right-2 px-3 py-1 rounded-full text-xs font-bold shadow-lg ${
                                  ad.badge_color === 'red' ? 'bg-red-500 text-white' :
                                  ad.badge_color === 'blue' ? 'bg-blue-500 text-white' :
                                  ad.badge_color === 'green' ? 'bg-green-500 text-white' :
                                  ad.badge_color === 'yellow' ? 'bg-yellow-400 text-gray-900' :
                                  ad.badge_color === 'purple' ? 'bg-purple-500 text-white' :
                                  'bg-orange-500 text-white'
                                }`}>
                                  {ad.badge_text}
                                </div>
                              )}
                            </div>
                            
                            {/* Advertisement Name - flexible height */}
                            <h3 className="text-base font-semibold text-gray-900 text-center mb-3 flex-grow px-1">{ad.name}</h3>
                            
                            {/* Custom Button */}
                            <button
                              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-2.5 px-4 rounded-lg opacity-90 hover:opacity-100 hover:from-blue-600 hover:to-purple-700 transition duration-200 font-medium text-sm"
                            >
                              {ad.button_text || t.getPromoCode}
                            </button>
                          </div>
                        </div>

                        {/* Mobile Layout - Compact horizontal */}
                        <div className="md:hidden p-4 flex items-center space-x-4">
                          {/* Provider Logo - Left side */}
                          <div className="flex-shrink-0">
                            {(() => {
                              const providerInfo = getProviderInfo(ad);
                              return providerInfo.logoUrl ? (
                                <img 
                                  src={providerInfo.logoUrl} 
                                  alt={`${providerInfo.name || 'Provider'} logo`}
                                  loading="lazy"
                                  decoding="async"
                                  className="w-12 h-12 rounded-lg object-cover"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    target.nextElementSibling?.classList.remove('hidden');
                                  }}
                                />
                              ) : null;
                            })()}
                            <div className={`bg-purple-100 p-2 rounded-lg ${(() => { const pi = getProviderInfo(ad); return pi.logoUrl ? 'hidden' : ''; })()}`}>
                              <Building2 className="text-purple-600" size={24} />
                            </div>
                          </div>
                          
                          {/* Text content - Right side */}
                          <div className="flex-1 min-w-0 flex items-center gap-2">
                            {/* Top 10% Star Badge for mobile */}
                            {top10PercentAdIds.has(ad.id) && (
                              <Star
                                size={16}
                                fill="#facc15"
                                stroke="#ca8a04"
                                className="flex-shrink-0 drop-shadow"
                              />
                            )}
                            {/* Advertisement Name */}
                            <h3 className="text-xs font-semibold text-gray-900 truncate">{ad.name}</h3>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </>
        )}
      </div>

      {/* Newsletter Subscription */}
      <NewsletterSubscription 
        t={t} 
        locale={locale} 
        campaignId={campaign?.id}
        region={campaign?.region}
      />

      {/* Footer */}
      <div className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center relative pb-6 md:pb-0 gap-4">
            <div className="text-left text-gray-500">
              <p className="text-sm">
                {poweredByContent}
              </p>
              <p className="text-xs mt-2 hidden md:block">
                {t.campaign}: {campaign.name} | {t.region}: {campaign.region}
              </p>
            </div>
            <div className="flex items-center text-sm space-x-3 whitespace-nowrap">
              <a href={`/terms-of-service?locale=${locale}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">{t.termsOfService}</a>
              <span className="text-gray-300">|</span>
              <a href={`/privacy-policy?locale=${locale}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">{t.privacyPolicy}</a>
            </div>

            {/* Mobile region label in bottom-right corner */}
            <p className="md:hidden text-xs text-gray-500 absolute right-0 bottom-0">
              {t.region}: {campaign.region}
            </p>
          </div>
        </div>
      </div>


      {/* Promo delivery notice modal - appears on top of ad modal */}
      {promoDeliveryNotice && promoDeliveryNotice.placement === 'modal' && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[60]">
          <div
            className={`max-w-md w-full rounded-2xl shadow-2xl p-6 text-gray-800 relative ${
              promoDeliveryNotice.type === 'success' || promoDeliveryNotice.type === 'info'
                ? 'bg-gradient-to-br from-green-50 via-green-100 to-green-200'
                : promoDeliveryNotice.type === 'error'
                  ? 'bg-gradient-to-br from-red-50 via-red-100 to-red-200'
                  : 'bg-gradient-to-br from-blue-500 to-blue-700'
            }`}
          >
            <button
              onClick={() => {
                setPromoDeliveryNotice(null);
                // For error notices, don't close the ad modal - only close the notice itself
                if (promoDeliveryNotice.type !== 'error') {
                  setSelectedAd(null);
                  setShowPromoForm(null);
                }
              }}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 transition"
            >
              ✕
            </button>
            {promoDeliveryNotice.title && (
              <h3 className="text-xl font-bold mb-2">{promoDeliveryNotice.title}</h3>
            )}
            <p className="text-base leading-relaxed mb-4">{promoDeliveryNotice.message}</p>
            <button
              onClick={() => {
                setPromoDeliveryNotice(null);
                // For error notices, don't close the ad modal - only close the notice itself
                if (promoDeliveryNotice.type !== 'error') {
                  setSelectedAd(null);
                  setShowPromoForm(null);
                }
              }}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-4 rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200 font-medium"
            >
              {t.close}
            </button>
          </div>
        </div>
      )}

      {/* Promo delivery notice banner (global placement) */}
      {promoDeliveryNotice && promoDeliveryNotice.placement === 'global' && (
        <div className="fixed top-4 inset-x-0 flex justify-center z-40 px-4">
          <div
            className={`max-w-xl w-full rounded-xl shadow-lg px-6 py-4 text-white ${
              promoDeliveryNotice.type === 'success'
                ? 'bg-green-600'
                : promoDeliveryNotice.type === 'error'
                  ? 'bg-red-600'
                  : 'bg-blue-600'
            }`}
          >
            {promoDeliveryNotice.title && (
              <p className="text-base font-semibold mb-1">{promoDeliveryNotice.title}</p>
            )}
            <p className="text-sm leading-relaxed">{promoDeliveryNotice.message}</p>
          </div>
        </div>
      )}

      {/* Advertisement Modal */}
      {selectedAd && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header with banner */}
            <div className="aspect-video bg-gray-100 relative overflow-hidden p-4">
              <img
                src={selectedAd.banner_url}
                alt={selectedAd.name}
                loading="eager"
                decoding="async"
                className="w-full h-full object-cover rounded-lg"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'https://images.pexels.com/photos/1267338/pexels-photo-1267338.jpeg?auto=compress&cs=tinysrgb&w=800';
                }}
              />
              <button
                onClick={() => setSelectedAd(null)}
                className="absolute top-6 right-6 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 transition duration-200"
              >
                ✕
              </button>
            </div>
            
            <div className="p-6">
              {/* Advertisement Details */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">{selectedAd.name}</h2>
                <div className="flex items-center">
                  {(() => {
                    // Get provider info (custom or partner fallback)
                    const providerInfo = getProviderInfo(selectedAd);
                    return providerInfo.logoUrl ? (
                      <img 
                        src={providerInfo.logoUrl} 
                        alt={`${providerInfo.name || 'Provider'} logo`}
                        className="w-16 h-16 rounded object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null;
                  })()}
                  <div className={`bg-purple-100 p-2 rounded ${(() => {
                    const providerInfo = getProviderInfo(selectedAd);
                    return providerInfo.logoUrl ? 'hidden' : '';
                  })()}`}>
                    <Building2 className="text-purple-600" size={16} />
                  </div>
                </div>
              </div>
              
              <div
                className="text-gray-600 mb-6 max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_li]:my-1 [&_p]:my-1"
                dangerouslySetInnerHTML={{ __html: formatDescriptionHtml(selectedAd.description) }}
              />
              
              {selectedAd.categories && selectedAd.categories.length > 0 && (
                <div className="mb-6 flex items-center flex-wrap gap-2">
                  <h4 className="text-sm font-medium text-gray-700">{t.categories}</h4>
                  {selectedAd.categories.map((category: string, index: number) => (
                    <span
                      key={index}
                      className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full"
                    >
                      {getCategoryTranslation(category, locale)}
                    </span>
                  ))}
                </div>
              )}
              
              <div className="flex items-center text-sm text-gray-500 mb-6">
                <Calendar size={16} className="mr-2" />
                <span>{t.validUntil} {new Date(selectedAd.end_date).toLocaleDateString(locale)}</span>
              </div>
              
              {/* Promocode Type Information */}
              {selectedAd.promocode_config && (
                <div className="mb-6">
                  {/* N/A - No Promocode */}
                  {selectedAd.promocode_config.type === 'none' && (
                    <div className="bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">📋 Direct Contact</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        You'll be contacted directly via your email address. No promocode will be generated.
                      </div>
                    </div>
                  )}

                  {/* BYOP (API) */}
                  {selectedAd.promocode_config.type === 'byop' && (
                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-orange-700">🔌 API Integration</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">Method:</span>
                          <span className="font-medium text-orange-700">{selectedAd.promocode_config.api_config?.method}</span>
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Endpoint:</span> {selectedAd.promocode_config.api_config?.endpoint}
                        </div>
                      </div>
                      <p className="text-sm text-orange-700 mt-3">
                        Connected to your existing promocode system
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Promo Code Section */}
              {selectedAd.isSubmitted ? (
                <div className="w-full bg-green-100 text-green-800 py-3 px-4 rounded-lg flex items-center justify-center font-medium">
                  <Check size={18} className="mr-2" />
                  {t.promoCodeRequested}
                </div>
              ) : showPromoForm === selectedAd.partnerCampaign.id ? (
                <div className="space-y-4">
                  <div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                  <div className="flex items-start space-x-3 py-2">
                    <div 
                      className="flex items-center cursor-pointer"
                      onClick={() => setAgreedToPrivacy(!agreedToPrivacy)}
                    >
                      <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                        agreedToPrivacy 
                          ? 'bg-blue-600 border-blue-600' 
                          : 'border-gray-300 bg-white'
                      }`}>
                        {agreedToPrivacy && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <label 
                      className="text-sm text-gray-600 cursor-pointer select-none flex-1"
                      onClick={() => setAgreedToPrivacy(!agreedToPrivacy)}
                    >
                      {t.agreeWithPrivacy}{' '}
                      <a
                        href={`/privacy-policy?locale=${locale}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t.platformPrivacyPolicy}
                      </a>
                      {' '}{t.and}{' '}
                      {(() => {
                        const providerInfo = getProviderInfo(selectedAd);
                        return providerInfo.privacyPolicyUrl ? (
                          <a 
                            href={providerInfo.privacyPolicyUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {t.partnerPrivacyPolicy}
                          </a>
                        ) : (
                          <span className="text-gray-400">{t.partnerPrivacyPolicy}</span>
                        );
                      })()}
                    </label>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setShowPromoForm(null);
                        setSelectedAd(null);
                        setEmail('');
                        setAgreedToPrivacy(false);
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200"
                    >
                      {t.cancel}
                    </button>
                    <button
                      onClick={() => handlePromoRequest(selectedAd.partnerCampaign.id, selectedAd.partnerCampaign.partner_id)}
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200"
                    >
                      {selectedAd.button_text || t.requestCode}
                    </button>
                  </div>
                </div>
              ) : (
                <button 
                  onClick={async () => {
                    setShowPromoForm(selectedAd.partnerCampaign.id);
                    
                    // Track partner link click when user shows interest
                    await pbClickTracker.trackPartnerLinkClick(
                      selectedAd.partnerCampaign.partner_id,
                      selectedAd.partnerCampaign.id,
                      selectedAd.id,
                      campaign?.id,
                      {
                        action: 'get_promo_code_clicked',
                        advertisement_name: selectedAd.name
                      }
                    );
                  }}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-4 rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200 flex items-center justify-center font-medium"
                >
                  <Mail size={18} className="mr-2" />
                  {selectedAd.button_text || t.getPromoCode}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showNoPromoConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {t.directContactTitle || 'Direct contact confirmation'}
            </h3>
            <p className="text-gray-700 mb-4">
              {t.directContactNotification || 'You will be contacted by the advertisement provider for further details.'}
            </p>
            <p className="text-gray-600 mb-6">
              {t.directContactConfirmQuestion || 'Do you want to proceed?'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCancelNoPromo}
                className="flex-1 px-4 py-3 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition"
              >
                {t.cancel}
              </button>
              <button
                onClick={handleConfirmNoPromo}
                className="flex-1 px-4 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
              >
                {t.proceed || 'Proceed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRedirectConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {t.redirectConfirmTitle || 'Redirect confirmation'}
            </h3>
            <p className="text-gray-700 mb-4">
              {t.redirectConfirmMessage || 'You will be redirected to the advertisement provider\'s website.'}
            </p>
            <p className="text-gray-600 mb-6">
              {t.redirectConfirmQuestion || 'Do you want to continue?'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCancelRedirect}
                className="flex-1 px-4 py-3 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition"
              >
                {t.cancel}
              </button>
              <button
                onClick={handleConfirmRedirect}
                className="flex-1 px-4 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
              >
                {t.proceed || 'Proceed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Promocode Modal */}
      {showPromocodeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative animate-fade-in">
            <button
              onClick={closePromocodeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
              ✕
            </button>

            {generatedPromocode === 'API_PENDING' ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {t.apiRequestSentTitle}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t.apiRequestSentDescription}
                </p>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-orange-800">
                    <strong>{t.apiRequestSentNoteTitle}</strong> {t.apiRequestSentNoteDescription}
                  </p>
                </div>
                <button
                  onClick={closePromocodeModal}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-3 px-6 rounded-lg hover:from-orange-600 hover:to-red-700 transition duration-200 font-medium"
                >
                  {t.close || 'Затвори'}
                </button>
              </div>
            ) : isDirectContactMessage ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-mail text-blue-600"
                  >
                    <path d="M3 8l9 6 9-6"></path>
                    <rect width="18" height="14" x="3" y="5" rx="2" ry="2"></rect>
                  </svg>
                </div>
                <p className="text-gray-700 text-base leading-relaxed">
                  {generatedPromocode}
                </p>
                <button
                  onClick={closePromocodeModal}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-6 rounded-lg hover:from-blue-600 hover:to-indigo-700 transition duration-200 font-medium"
                >
                  {t.close || 'Готово'}
                </button>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-check-circle text-green-600"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <path d="m9 11 3 3L22 4"></path>
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">🎉 Вашият промо код!</h3>
                <p className="text-gray-600 mb-6">
                  Ето вашия ексклузивен промо код. Копирайте го и го използвайте при плащане!
                </p>
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
                  <div className="text-sm text-gray-600 mb-2">Вашият промо код:</div>
                  <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-4 font-mono tracking-wider">
                    {generatedPromocode}
                  </div>
                  <button
                    onClick={() => copyToClipboard(generatedPromocode ?? '')}
                    className="flex items-center justify-center space-x-2 mx-auto px-4 py-2 rounded-lg transition duration-200 bg-blue-100 text-blue-700 hover:bg-blue-200"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="lucide lucide-copy"
                    >
                      <rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect>
                      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
                    </svg>
                    <span>{copiedToClipboard ? 'Копирано!' : 'Копирай код'}</span>
                  </button>
                </div>
                
                {/* Target URL Button - Link to partner's page */}
                {promoTargetUrl && (
                  <div className="mb-6">
                    <a
                      href={promoTargetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 px-4 rounded-lg hover:from-green-600 hover:to-emerald-700 transition duration-200 flex items-center justify-center font-medium"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="mr-2"
                      >
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                      {t.useYourCode || 'Use Your Code →'}
                    </a>
                    <p className="text-xs text-gray-500 text-center mt-2">
                      {t.visitPartnerPage || 'Visit the partner\'s page to redeem your code'}
                    </p>
                  </div>
                )}
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-800">
                    {promoEmailReminder
                      ? promoEmailReminder
                      : <><strong>{t.promocodeTipTitle}</strong> {t.promocodeTipDescription}</>}
                  </p>
                </div>
                <div className="space-y-3">
                  <button
                    onClick={closePromocodeModal}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-4 rounded-lg hover:from-blue-600 hover:to-purple-700 transition duration-200 flex items-center justify-center font-medium"
                  >
                    Готово
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};